import ExpenseMessageFormConfig from "./form";
import GenericForm from "../GenericForm";
import { ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { useRef, useState, useEffect } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import LoadXmlDataForm from "@components/dynamic/LoadXmlDataForm";
import { generateATVDocumentKey, generateATVDocumentSequence, generateATVDocumentSequenceNumber, loadXmlDocument, XmlSummaryData } from "@utils/EDocument";
import LoadingScreen from "@components/content/LoadingScreen";
import { Button, Card, CardText, Col, Label, Row } from "reactstrap";
import { ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE, SITUACION_COMPROBANTE_NORMAL } from "@utils/Constants";
import { getCurrentDateTime } from "@utils/Api";
import { useAppSelector } from "@/store/hooks";
import { ApiError, NotFoundError } from "@/api/ApiClient";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { MessageMetadata } from "@/atv/eInvoiceApi";
import { DocumentSequenceApi } from "@/api/DocumentSequenceApi";
import { AtvMessageApi, AtvMessageDataType } from "@/api/AtvMessageApi";
import { showAlert } from "@utils/Alert";
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { ExpenseMessageFormData } from "@/formTypes/ExpenseMessageFormDataType";
import { ExpenseApi, ExpenseDataType } from "@/api/ExpenseApi";
import { omitProperties } from "@utils/Utils";
import { loadReferenceFormLabel } from "@utils/Forms";
import { ReceivedInvoiceMessageData } from "@/formTypes/ReceivedInvoiceDataType";
import { markRecieveInvoiceAsValidated } from "@utils/RecieveEmailUtils";

type ExpenseMessageFormProps = {
  formId: string,
  saveRedirectUrl: string
}

const ExpenseMessageForm = (props: ExpenseMessageFormProps) => {
  const formRef = useRef<DynamicFormRef>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceLoaded, setInvoiceLoaded] = useState<boolean>(false);
  const [providerInvoiceData, setProviderInvoiceData] = useState<ProviderInvoiceDataType>();
  const [documentSummary, setDocumentSummary] = useState<XmlSummaryData>({} as XmlSummaryData);
  const suscriptionData = useAppSelector(state => state.suscription.suscription);
  const [formConfig, setFormConfig] = useState<any>();
  const [receivedEmailId, setReceivedEmailId] = useState<string>();

  useEffect(() => {
    loadFormConfig()
  }, [suscriptionData])

  const loadFormConfig = () => {
    const formConfig = loadReferenceFormLabel(ExpenseMessageFormConfig, suscriptionData);
    if (suscriptionData?.id) {
      setFormConfig(formConfig)
    }
  }

  const onXMLFileLoad = async (fileData: any, receivedEmailId?: string) => {
    setIsLoading(true)
    try {
      if (receivedEmailId) {
        setReceivedEmailId(receivedEmailId);
      }
      const { messageData, providerInvoiceData, documentSummary } = await loadXmlDocument(fileData, ["FacturaElectronica"])
      setProviderInvoiceData(providerInvoiceData)
      setInvoiceLoaded(true)
      setDocumentSummary(documentSummary as XmlSummaryData)

      const defaultActivityCodeCurrency = await ActivityCodeApi.getDefaultActivityCode()

      formRef.current?.resetFormValue({
        ...messageData,
        activitycode: messageData?.activitycode || defaultActivityCodeCurrency?.code
      });
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message)
      }
    }
    finally {
      setIsLoading(false)
    }
  }

  const saveExpenseData = async (expenseData: ExpenseDataType): Promise<ExpenseDataType> => {
    let expense;

    try {
      expense = await ExpenseApi.getExpenseByDocumentKey(`${expenseData.document_key}`);
    } catch (err) {
      if (err instanceof NotFoundError) {
        //Expense invoices not found create it
        expense = await ExpenseApi.create(expenseData);
      } else {
        throw err
      }
    }
    return expense
  }

  const validateInvoice = async (data: ExpenseMessageFormData): Promise<ExpenseMessageFormData> => {
    if (suscriptionData && providerInvoiceData) {

      const expenseData: ExpenseDataType = {
        ...providerInvoiceData,
        reference_number: data.reference_number,
        detalle: data.detalle,
        expenseCategoryId: data.expenseCategoryId,
        monto: providerInvoiceData.total,
        identificacion: `${documentSummary.emisor?.Identificacion?.Numero}`,
        nombre: `${documentSummary.emisor?.Nombre}`,
      }

      const expenseFilteredData = omitProperties(expenseData, ["total", "tipo", "pagada", "numero", "providerId"], true);

      const savedExpense = await saveExpenseData(expenseFilteredData)

      const sequence = await generateATVDocumentSequence(ATV_RESPONSES_CODE_TO_EDOCUMET_TYPE[`${data.mensaje}`])
      const sequenceNumber = generateATVDocumentSequenceNumber(sequence);
      const documentKey = generateATVDocumentKey({
        fecha: getCurrentDateTime(),
        sequence: sequenceNumber
      }, suscriptionData, SITUACION_COMPROBANTE_NORMAL, "fecha")

      const atvMessage: AtvMessageDataType = {
        documentKey: documentKey,
        sequence: `${sequenceNumber}`,
        numeroCedulaEmisor: `${documentSummary.emisor?.Identificacion?.Numero}`,
        fechaEmisionDoc: `${documentSummary.fechaEmision}`,
        mensaje: `${data.mensaje}`,
        detalleMensaje: data.detalle || "",
        montoTotalImpuesto: Number(data.impuestoAcreditar || 0),
        codigoActividad: `${data.activitycode}`,
        condicionImpuesto: `${data.condicionImpuesto}`,
        montoTotalImpuestoAcreditar: Number(data.impuestoAcreditar || 0),
        montoTotalDeGastoAplicable: Number(data.gastoAplicable || 0),
        totalFactura: Number(documentSummary.totalComprobanteAmount || 0),
        providerId: "none",
        expenseId: savedExpense.id || ""
      }

      const savedMessage = await AtvMessageApi.create(atvMessage);

      const messageBody: Partial<MessageMetadata> = {
        ...atvMessage,
        id: savedMessage.id,
        clave: `${providerInvoiceData.document_key}`,
        numeroCedulaReceptor: `${documentSummary.receptor?.Identificacion?.Numero}`,
        numeroConsecutivoReceptor: sequenceNumber,
        tipo_identificacion: `${documentSummary.tipo_identificacion}`,
        identificacion: `${documentSummary.identificacion}`.padStart(2, '0'),
        metadata: {
          calltime: Date.now(),
          numeroConsecutivo: sequenceNumber,
          documentType: "Message",
          clave: documentKey,
          fechaEmisionUTC: `${documentSummary.fechaEmision}`
        }
      }

      const easyposEInvoice = new EasyposEInvoice(suscriptionData)
      await easyposEInvoice.submitMessage(messageBody);
      await DocumentSequenceApi.save(sequence)

      showAlert("success", `El mensaje a tributacion fue enviado correctamente y la gasto ha sido agregado correctamente`)
      if (receivedEmailId) {
        const documentData: Partial<ReceivedInvoiceMessageData> = {
          documentId: expenseData.id,
          documentType: "expense",
          reference_number: expenseData.reference_number,
        }
        setReceivedEmailId("")
        await markRecieveInvoiceAsValidated(receivedEmailId, messageBody, documentData)
      }
      return data
    }
    throw new ApiError("No fue posible generar el consecutivo del Mensaje", "API_ERROR");
  }

  return (
    <>
      {formConfig?.sections && (<>
        <LoadXmlDataForm
          paramLoadable={true}
          onFileLoaded={onXMLFileLoad}
        />
        <GenericForm<ExpenseMessageFormData, ExpenseMessageFormData>
          ref={formRef}
          formId={props.formId}
          saveRedirectUrl={props.saveRedirectUrl}
          formConfig={ExpenseMessageFormConfig}
          createFn={(...arg) => validateInvoice(...arg)}
          successMessage="La factura fue validada correctamente"
          errorMessage="No fue posible validar la factura correctamente"
        />
        {invoiceLoaded && (
          <>
            <Row className="g-1 mb-4">
              <Col md={{
                offset: 1,
                size: 9
              }}>
                <Card
                  className="mt-3"
                  body
                  color="light"
                >
                  <CardText>
                    <h3 className="text-center mb-3 mt-3">Emisor: {documentSummary.providerName}</h3>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Numero Factura:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.documentKey}
                        </Label>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Total Impuestos:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.totalImpuesto}
                        </Label>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={{
                        offset: 2,
                        size: 3
                      }}>
                        <b >
                          Total Factura:
                        </b>
                      </Col>
                      <Col md>
                        <Label>
                          {documentSummary.totalComprobante}
                        </Label>
                      </Col>
                    </Row>
                  </CardText>
                </Card>
              </Col >
            </Row >
            <div className="d-flex justify-content-center">
              <Button color="primary" size="lg" type="submit" form={props.formId}>
                Enviar Mensaje
              </Button>
            </div>
          </>
        )}

        <LoadingScreen isLoading={isLoading} />
      </>
      )}
    </>
  )
}

export default ExpenseMessageForm;
